/**
 * Cross-device account sync: records, stats, settings, achievements toast state, sandbox presets.
 */
import { fetchAccountData, patchAccountData } from "../lib/account-data";
import { fetchMyBests, fetchMyLeaderboardRank } from "../lib/cloud";
import {
  ACCOUNT_DATA_VERSION,
  asPayloadV1,
  emptyAccountPayload,
  type AccountDataPayloadV1,
  type AccountRecordsBests,
  type AccountSandboxPresetRow,
} from "../lib/accountDataTypes";
import { applyDotsFromAccountPayload, buildDotsAccountSlice, getDotsMutationGen } from "./dots-currency";
import {
  applyPlayStreaksFromAccount,
  clearPlayStreaksLocal,
  getPlayStreaksState,
  mergePlayStreaks,
  setPlayStreakAccountPushScheduler,
  syncPlayStreaksFromServer,
} from "./play-streaks";
import { apiPath } from "../lib/apiPath";

const KEY_BEST_NORMAL = ["bestNormal", "bestScoreNormal"];
const KEY_BEST_WASD = ["bestWASD", "bestScoreWASD"];
const KEY_BEST_SUPER = ["bestSuper", "bestScoreSuperDot", "bestScoreSuper"];
const KEY_BEST_PROG = ["bestProg", "bestLevelProg"];
const KEY_BEST_ROULETTE = ["bestRoulette", "bestScoreRoulette"];
const KEY_BEST_SANDBOX = ["bestSandbox", "bestScoreSandbox"];
const KEY_BEST_HIGHWAY = ["bestHighway", "bestScoreHighway"];
const KEY_BEST_DARKNESS = ["bestDarkness", "bestScoreDarkness"];
const KEY_BEST_MIRROR = ["bestMirror", "bestScoreMirror"];
const KEY_BEST_SNAKE = ["bestSnake", "bestScoreSnake"];
const KEY_BEST_COMBO = ["bestCombo", "bestScoreCombo"];
const KEY_BEST_OVERLOAD = ["bestOverload", "bestScoreOverload"];
const KEY_BEST_COLOR_RUSH = ["bestColorRush", "bestScoreColorRush"];
const KEY_BEST_HEAT = ["bestHeat", "bestScoreHeat"];
const KEY_BEST_HUNTER = ["bestHunter", "bestScoreHunter"];
const KEY_BEST_POP = ["bestPop", "bestScorePop"];
const KEY_BEST_COUNTER = ["bestCounter", "bestScoreCounter"];
const KEY_BEST_AIM = ["bestAim", "bestScoreAim"];
const KEY_BEST_HOOK = ["bestHook", "bestScoreHook"];
const KEY_BEST_TILT = ["bestTilt", "bestScoreTilt"];
const KEY_BEST_ORBIT = ["bestOrbit", "bestScoreOrbit"];
const KEY_BEST_DELAY = ["bestDelay", "bestScoreDelay"];
const KEY_PLAYTIME = ["playTime", "totalPlayTime"];
const KEY_DEATHS = ["deathCount", "totalDeaths"];
const KEY_SPEEDRUN_BEST = "bestSpeedrunTimesV4";
const KEY_SANDBOX_SPEEDRUN_LOCAL = "sandboxSpeedrunLocalBestsV1";
const KEY_ACHIEVEMENT_TOAST_KNOWN_IDS = "achievementToastKnownIdsV1";
const SANDBOX_PRESETS_KEY = "sandboxPresets";
const SANDBOX_SAVE_IS_PUBLIC_KEY = "sandboxSaveIsPublic";
const KEY_SETTINGS_UPDATED_AT = "accountSettingsUpdatedAt";

export const ACCOUNT_SETTINGS_KEYS = [
  "settingBallsColor",
  "settingCursorColor",
  "settingButtonsColor",
  "settingButtonsAutoText",
  "settingTextColor",
  "settingTrailColor",
  "settingMenuBackgroundColor",
  "settingGameBackgroundColor",
  "settingMenuBackgroundBright",
  "settingGameBackgroundBright",
  "settingWASDSensitivity",
  "settingMusicVolume",
  "settingGameMusicVolume",
  "settingMenuSong",
  "settingGameSong",
  "settingBounceVolume",
  "settingPickupVolume",
  "settingGameSfxVolume",
  "settingDarknessVolume",
  "settingUiVolume",
  "settingEffectFlashes",
  "settingEffectShake",
  "settingEffectParticles",
  "settingFreezeOnLose",
  "settingFreezeOnLoseSeconds",
  "settingQuickRestart",
  "settingModesSelectPanel",
  "settingPlayTransition",
  "settingTrailEnabled",
  "settingCosmeticsMatchCursor",
  "settingNeonBalls",
  "settingHomeAmbientBalls",
  "settingNewsInHome",
  "settingNotificationsInHome",
  "settingHomeSplash",
  "settingHomeShowFriends",
  "settingHomeFriendsFilter",
  "settingHomeShowLastMode",
  "settingHomeCursorBounce",
  "settingHomeShowTrending",
  "settingHomeShowDots",
  "settingHomeShowStreak",
  "settingHomeShowChallenge",
  "settingHomeShowLootDrops",
  "settingHomeShowNewInShop",
  "settingHomeShowSuggested",
  "settingHomeShowContinue",
  "settingHomeLayoutV1",
  "settingSounds",
  "settingTouchCursorJoystick",
  "settingTouchCursorJoystickVisible",
  "settingTouchJoystickSensitivity",
  "settingShowKeybindIndicator",
  "settingHoldReturnHome",
  "settingHoldReturnHomeSeconds",
  "keybindReturn",
  "keybindPause",
  "keybindMenu",
  "keybindPlayAgain",
  "keybindNextLevel",
  "keybindQuickRestart",
  "dpadInsetRight",
  "dpadInsetBottom",
  "dpadInsetLeft",
  "dpadLayout",
  "dpadButtonOffsets",
  "dpadLayout3Pos",
  "settingDpadEightDirections",
  "dpadButtonScale",
  "dpadButtonOpacity",
  "settingDpadGridSnap",
  "settingRoulettePhotoAck",
];

const BEST_BY_MODE: { mode: keyof AccountRecordsBests; keys: string[] }[] = [
  { mode: "normal", keys: KEY_BEST_NORMAL },
  { mode: "wasd", keys: KEY_BEST_WASD },
  { mode: "superdot", keys: KEY_BEST_SUPER },
  { mode: "progression", keys: KEY_BEST_PROG },
  { mode: "roulette", keys: KEY_BEST_ROULETTE },
  { mode: "sandbox", keys: KEY_BEST_SANDBOX },
  { mode: "highway", keys: KEY_BEST_HIGHWAY },
  { mode: "darkness", keys: KEY_BEST_DARKNESS },
  { mode: "mirror", keys: KEY_BEST_MIRROR },
  { mode: "snake", keys: KEY_BEST_SNAKE },
  { mode: "combo", keys: KEY_BEST_COMBO },
  { mode: "overload", keys: KEY_BEST_OVERLOAD },
  { mode: "colorrush", keys: KEY_BEST_COLOR_RUSH },
  { mode: "heat", keys: KEY_BEST_HEAT },
  { mode: "hunter", keys: KEY_BEST_HUNTER },
  { mode: "pop", keys: KEY_BEST_POP },
  { mode: "counter", keys: KEY_BEST_COUNTER },
  { mode: "aim", keys: KEY_BEST_AIM },
  { mode: "hook", keys: KEY_BEST_HOOK },
  { mode: "tilt", keys: KEY_BEST_TILT },
  { mode: "orbit", keys: KEY_BEST_ORBIT },
  { mode: "delay", keys: KEY_BEST_DELAY },
];

function readStored(keys: string[]): number {
  for (const key of keys) {
    const raw = localStorage.getItem(key);
    if (raw !== null) return Number(raw) || 0;
  }
  return 0;
}

function writeStored(keys: string[], value: number) {
  for (const key of keys) localStorage.setItem(key, String(value));
}

function num(v: unknown): number | undefined {
  if (v == null || !Number.isFinite(Number(v))) return undefined;
  return Math.floor(Number(v));
}

function maxNum(a: number | undefined, b: number | undefined): number | undefined {
  if (a == null) return b;
  if (b == null) return a;
  return Math.max(a, b);
}

function mergeBests(a: AccountRecordsBests = {}, b: AccountRecordsBests = {}): AccountRecordsBests {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]) as Set<keyof AccountRecordsBests>;
  const out: AccountRecordsBests = {};
  for (const k of keys) {
    const m = maxNum(num(a[k]), num(b[k]));
    if (m != null && m > 0) out[k] = m;
  }
  return out;
}

function mergeTimeMap(
  a: Record<string, number> = {},
  b: Record<string, number> = {},
): Record<string, number> {
  const out: Record<string, number> = { ...a };
  for (const [k, raw] of Object.entries(b)) {
    const v = num(raw);
    if (v == null || v <= 0) continue;
    const prev = num(out[k]);
    out[k] = prev != null && prev > 0 ? Math.min(prev, v) : v;
  }
  return out;
}

function mergeSettings(
  a: Record<string, string> = {},
  b: Record<string, string> = {},
  aAt: string | null | undefined,
  bAt: string | null | undefined,
): { settings: Record<string, string>; settingsUpdatedAt: string | null } {
  const ta = aAt ? Date.parse(aAt) : NaN;
  const tb = bAt ? Date.parse(bAt) : NaN;
  const aValid = Number.isFinite(ta);
  const bValid = Number.isFinite(tb);
  if (aValid && bValid) {
    if (ta === tb) return { settings: { ...b, ...a }, settingsUpdatedAt: aAt ?? bAt ?? null };
    return ta > tb
      ? { settings: { ...a }, settingsUpdatedAt: aAt ?? null }
      : { settings: { ...b }, settingsUpdatedAt: bAt ?? null };
  }
  if (aValid) return { settings: { ...a }, settingsUpdatedAt: aAt ?? null };
  if (bValid) return { settings: { ...b }, settingsUpdatedAt: bAt ?? null };
  return { settings: { ...b, ...a }, settingsUpdatedAt: null };
}

function mergePresets(
  a: AccountSandboxPresetRow[] = [],
  b: AccountSandboxPresetRow[] = [],
): AccountSandboxPresetRow[] {
  const byName = new Map<string, AccountSandboxPresetRow>();
  const put = (row: AccountSandboxPresetRow) => {
    if (!row || typeof row.name !== "string") return;
    const key = row.name.trim().toLowerCase();
    if (!key) return;
    const prev = byName.get(key);
    if (!prev) {
      byName.set(key, row);
      return;
    }
    const prevMs = num(prev.lastSpeedrunMs) ?? 0;
    const nextMs = num(row.lastSpeedrunMs) ?? 0;
    const pick = nextMs > 0 && (prevMs <= 0 || nextMs < prevMs) ? row : prev;
    byName.set(key, {
      name: pick.name,
      config: pick.config ?? prev.config,
      cloudPresetId: pick.cloudPresetId || prev.cloudPresetId,
      lastSpeedrunMs: prevMs > 0 && nextMs > 0 ? Math.min(prevMs, nextMs) : maxNum(prevMs, nextMs),
    });
  };
  for (const row of a) put(row);
  for (const row of b) put(row);
  return [...byName.values()];
}

function unionIds(a: string[] = [], b: string[] = []): string[] {
  return [...new Set([...a, ...b].filter((x) => typeof x === "string" && x.length > 0))];
}

function mergePlayStreaksPayload(
  a: AccountDataPayloadV1["playStreaks"],
  b: AccountDataPayloadV1["playStreaks"],
): AccountDataPayloadV1["playStreaks"] {
  return mergePlayStreaks(a, b);
}

export function mergeAccountPayloads(
  local: AccountDataPayloadV1,
  remote: AccountDataPayloadV1,
): AccountDataPayloadV1 {
  const a = local || emptyAccountPayload();
  const b = remote || emptyAccountPayload();
  const settingsMerge = mergeSettings(
    a.settings ?? {},
    b.settings ?? {},
    a.settingsUpdatedAt,
    b.settingsUpdatedAt,
  );
  return {
    version: ACCOUNT_DATA_VERSION,
    records: {
      bests: mergeBests(a.records?.bests, b.records?.bests),
      speedrunTimes: mergeTimeMap(a.records?.speedrunTimes, b.records?.speedrunTimes),
      sandboxSpeedrunLocal: mergeTimeMap(
        a.records?.sandboxSpeedrunLocal,
        b.records?.sandboxSpeedrunLocal,
      ),
    },
    stats: {
      totalPlayTimeSec: maxNum(num(a.stats?.totalPlayTimeSec), num(b.stats?.totalPlayTimeSec)),
      totalDeaths: maxNum(num(a.stats?.totalDeaths), num(b.stats?.totalDeaths)),
    },
    settings: settingsMerge.settings,
    settingsUpdatedAt: settingsMerge.settingsUpdatedAt,
    achievements: {
      toastKnownIds: unionIds(a.achievements?.toastKnownIds, b.achievements?.toastKnownIds),
    },
    sandbox: {
      localPresets: mergePresets(a.sandbox?.localPresets, b.sandbox?.localPresets),
      saveIsPublic: !!(a.sandbox?.saveIsPublic || b.sandbox?.saveIsPublic),
    },
    dots: b.dots ?? a.dots,
    playStreaks: mergePlayStreaksPayload(a.playStreaks, b.playStreaks),
    playStreakTimezone: b.playStreakTimezone || a.playStreakTimezone,
    challenges: b.challenges ?? a.challenges,
    profile: b.profile ?? a.profile,
  };
}

function readSpeedrunJson(): Record<string, number> {
  try {
    const raw = localStorage.getItem(KEY_SPEEDRUN_BEST);
    if (!raw) return {};
    const o = JSON.parse(raw);
    if (!o || typeof o !== "object") return {};
    const out: Record<string, number> = {};
    for (const [k, v] of Object.entries(o)) {
      const n = num(v);
      if (n != null && n > 0) out[k] = n;
    }
    return out;
  } catch {
    return {};
  }
}

function readSandboxSpeedrunLocalJson(): Record<string, number> {
  try {
    const raw = localStorage.getItem(KEY_SANDBOX_SPEEDRUN_LOCAL);
    if (!raw) return {};
    const o = JSON.parse(raw);
    if (!o || typeof o !== "object") return {};
    const out: Record<string, number> = {};
    for (const [k, v] of Object.entries(o)) {
      const n = num(v);
      if (n != null && n > 0) out[k] = n;
    }
    return out;
  } catch {
    return {};
  }
}

function readToastKnownIds(): string[] {
  try {
    const raw = localStorage.getItem(KEY_ACHIEVEMENT_TOAST_KNOWN_IDS);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr.filter((x) => typeof x === "string") : [];
  } catch {
    return [];
  }
}

function readLocalSandboxPresets(): AccountSandboxPresetRow[] {
  try {
    const raw = localStorage.getItem(SANDBOX_PRESETS_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(arr)) return [];
    return arr
      .filter((item) => item && typeof item.name === "string" && item.config)
      .map((item) => {
        const cloudPresetId =
          item.cloudPresetId != null && String(item.cloudPresetId).trim()
            ? String(item.cloudPresetId).trim()
            : undefined;
        const lastMs = num(item.lastSpeedrunMs);
        return {
          name: item.name,
          config: item.config,
          ...(cloudPresetId ? { cloudPresetId } : {}),
          ...(lastMs != null && lastMs > 0 ? { lastSpeedrunMs: lastMs } : {}),
        };
      });
  } catch {
    return [];
  }
}

export function collectLocalAccountPayload(): AccountDataPayloadV1 {
  const bests: AccountRecordsBests = {};
  for (const { mode, keys } of BEST_BY_MODE) {
    const v = readStored(keys);
    if (v > 0) bests[mode] = v;
  }
  const settings: Record<string, string> = {};
  for (const k of ACCOUNT_SETTINGS_KEYS) {
    const v = localStorage.getItem(k);
    if (v !== null) settings[k] = v;
  }
  const settingsUpdatedAt = localStorage.getItem(KEY_SETTINGS_UPDATED_AT);
  return {
    version: ACCOUNT_DATA_VERSION,
    records: {
      bests,
      speedrunTimes: readSpeedrunJson(),
      sandboxSpeedrunLocal: readSandboxSpeedrunLocalJson(),
    },
    stats: {
      totalPlayTimeSec: readStored(KEY_PLAYTIME),
      totalDeaths: readStored(KEY_DEATHS),
    },
    settings,
    settingsUpdatedAt: settingsUpdatedAt || null,
    achievements: { toastKnownIds: readToastKnownIds() },
    sandbox: {
      localPresets: readLocalSandboxPresets(),
      saveIsPublic: localStorage.getItem(SANDBOX_SAVE_IS_PUBLIC_KEY) === "1",
    },
    dots: buildDotsAccountSlice(),
    playStreaks: getPlayStreaksState(),
  };
}

export function applyAccountPayloadToLocal(
  payload: AccountDataPayloadV1,
  opts?: { fullReplace?: boolean; applyDots?: boolean },
): void {
  const p = asPayloadV1(payload);
  /** Dots are mutated via /api/dots (and shop). Stale account-data merges must not clobber them. */
  const shouldApplyDots = opts?.fullReplace || opts?.applyDots === true;

  if (opts?.fullReplace) {
    for (const { mode, keys } of BEST_BY_MODE) {
      writeStored(keys, num(p.records?.bests?.[mode]) ?? 0);
    }
    localStorage.setItem(KEY_SPEEDRUN_BEST, JSON.stringify(p.records?.speedrunTimes ?? {}));
    localStorage.setItem(
      KEY_SANDBOX_SPEEDRUN_LOCAL,
      JSON.stringify(p.records?.sandboxSpeedrunLocal ?? {}),
    );
    writeStored(KEY_PLAYTIME, num(p.stats?.totalPlayTimeSec) ?? 0);
    writeStored(KEY_DEATHS, num(p.stats?.totalDeaths) ?? 0);
    for (const k of ACCOUNT_SETTINGS_KEYS) {
      const v = p.settings?.[k];
      if (typeof v === "string") localStorage.setItem(k, v);
      else localStorage.removeItem(k);
    }
    if (p.settingsUpdatedAt) localStorage.setItem(KEY_SETTINGS_UPDATED_AT, p.settingsUpdatedAt);
    else localStorage.removeItem(KEY_SETTINGS_UPDATED_AT);
    localStorage.setItem(
      KEY_ACHIEVEMENT_TOAST_KNOWN_IDS,
      JSON.stringify(p.achievements?.toastKnownIds ?? []),
    );
    localStorage.setItem(SANDBOX_PRESETS_KEY, JSON.stringify(p.sandbox?.localPresets ?? []));
    localStorage.setItem(SANDBOX_SAVE_IS_PUBLIC_KEY, p.sandbox?.saveIsPublic ? "1" : "0");
    if (shouldApplyDots) applyDotsFromAccountPayload(p);
    applyPlayStreaksFromAccount(p, { replace: true });
    return;
  }

  const pPartial = p;
  for (const { mode, keys } of BEST_BY_MODE) {
    const v = num(pPartial.records?.bests?.[mode]);
    if (v != null && v > 0) writeStored(keys, v);
  }
  const speedrun = pPartial.records?.speedrunTimes ?? {};
  if (Object.keys(speedrun).length) {
    localStorage.setItem(KEY_SPEEDRUN_BEST, JSON.stringify(speedrun));
  }
  const sbLocal = pPartial.records?.sandboxSpeedrunLocal ?? {};
  if (Object.keys(sbLocal).length) {
    localStorage.setItem(KEY_SANDBOX_SPEEDRUN_LOCAL, JSON.stringify(sbLocal));
  }
  const pt = num(pPartial.stats?.totalPlayTimeSec);
  if (pt != null && pt >= 0) writeStored(KEY_PLAYTIME, pt);
  const deaths = num(pPartial.stats?.totalDeaths);
  if (deaths != null && deaths >= 0) writeStored(KEY_DEATHS, deaths);
  if (pPartial.settings) {
    for (const [k, v] of Object.entries(pPartial.settings)) {
      if (typeof v === "string") localStorage.setItem(k, v);
    }
  }
  if (pPartial.settingsUpdatedAt) {
    localStorage.setItem(KEY_SETTINGS_UPDATED_AT, pPartial.settingsUpdatedAt);
  }
  const toastIds = pPartial.achievements?.toastKnownIds;
  if (toastIds && toastIds.length) {
    localStorage.setItem(
      KEY_ACHIEVEMENT_TOAST_KNOWN_IDS,
      JSON.stringify(unionIds(readToastKnownIds(), toastIds)),
    );
  }
  const presets = pPartial.sandbox?.localPresets;
  if (presets && presets.length) {
    localStorage.setItem(SANDBOX_PRESETS_KEY, JSON.stringify(presets));
  }
  if (pPartial.sandbox?.saveIsPublic != null) {
    localStorage.setItem(SANDBOX_SAVE_IS_PUBLIC_KEY, pPartial.sandbox.saveIsPublic ? "1" : "0");
  }
  // Skip dots on partial merges — a pull started before run-reward / LootDrop spend
  // would otherwise restore a stale higher/lower balance over the live one.
  if (shouldApplyDots && pPartial.dots) applyDotsFromAccountPayload(pPartial);
  if (pPartial.playStreaks) applyPlayStreaksFromAccount(pPartial);
}

export function touchAccountSettingsUpdatedAt(): void {
  localStorage.setItem(KEY_SETTINGS_UPDATED_AT, new Date().toISOString());
}

/** Wipe account-bound localStorage (guest / previous account data on this device). */
export function clearLocalAccountData(): void {
  for (const { keys } of BEST_BY_MODE) writeStored(keys, 0);
  localStorage.setItem(KEY_SPEEDRUN_BEST, "{}");
  localStorage.setItem(KEY_SANDBOX_SPEEDRUN_LOCAL, "{}");
  writeStored(KEY_PLAYTIME, 0);
  writeStored(KEY_DEATHS, 0);
  for (const k of ACCOUNT_SETTINGS_KEYS) localStorage.removeItem(k);
  localStorage.removeItem(KEY_SETTINGS_UPDATED_AT);
  localStorage.setItem(KEY_ACHIEVEMENT_TOAST_KNOWN_IDS, "[]");
  localStorage.setItem(SANDBOX_PRESETS_KEY, "[]");
  localStorage.setItem(SANDBOX_SAVE_IS_PUBLIC_KEY, "0");
  clearPlayStreaksLocal();
}

/** Clear synced account data on this device and drop sync session metadata. */
export function clearAccountSessionLocal(): void {
  clearLocalAccountData();
  clearLastSyncedUserId();
}

async function enrichWithCloudLeaderboardBests(payload: AccountDataPayloadV1): Promise<AccountDataPayloadV1> {
  const out = asPayloadV1(payload);
  try {
    const [sr, sb] = await Promise.all([
      fetch(apiPath("/api/leaderboard?mode=speedrun&mine=1&limit=1"), { credentials: "include" }),
      fetch(apiPath("/api/leaderboard?mode=sandbox_speedrun&mine=1&limit=1"), { credentials: "include" }),
    ]);
    const srData = (await sr.json()) as { speedrunPersonalBests?: Record<string, number> };
    const sbData = (await sb.json()) as { sandboxSpeedrunPersonalBests?: Record<string, number> };
    const cloudSr = srData.speedrunPersonalBests ?? {};
    const cloudSb: Record<string, number> = {};
    for (const [presetId, ms] of Object.entries(sbData.sandboxSpeedrunPersonalBests ?? {})) {
      cloudSb[`cloud:${presetId}`] = Number(ms) || 0;
    }
    out.records = {
      ...out.records,
      speedrunTimes: mergeTimeMap(out.records?.speedrunTimes, cloudSr),
      sandboxSpeedrunLocal: mergeTimeMap(out.records?.sandboxSpeedrunLocal, cloudSb),
    };
  } catch {
    /* optional */
  }
  return out;
}

/** Pull cloud leaderboard bests + account stats into localStorage (stats match Rankings). */
export async function syncLocalStatsFromLeaderboard(): Promise<void> {
  if (!isAccountSyncReady()) return;
  try {
    const bests = await fetchMyBests();
    for (const { mode, keys } of BEST_BY_MODE) {
      const entry = bests[mode];
      const myValue = entry?.value;
      const v = myValue != null && Number.isFinite(myValue) && myValue > 0 ? Math.floor(myValue) : 0;
      writeStored(keys, v);
    }
  } catch {
    // Fallback: legacy per-mode myRank (kept for compat if my-bests unavailable).
    await Promise.all(
      BEST_BY_MODE.map(async ({ mode, keys }) => {
        try {
          const { myValue } = await fetchMyLeaderboardRank(mode);
          const v = myValue != null && Number.isFinite(myValue) && myValue > 0 ? Math.floor(myValue) : 0;
          writeStored(keys, v);
        } catch {
          /* optional */
        }
      }),
    );
  }

  try {
    let remote = (await fetchAccountData()) ?? emptyAccountPayload();
    remote = await enrichWithCloudLeaderboardBests(remote);
    localStorage.setItem(KEY_SPEEDRUN_BEST, JSON.stringify(remote.records?.speedrunTimes ?? {}));
    localStorage.setItem(
      KEY_SANDBOX_SPEEDRUN_LOCAL,
      JSON.stringify(remote.records?.sandboxSpeedrunLocal ?? {}),
    );
    const deaths = num(remote.stats?.totalDeaths);
    const playtime = num(remote.stats?.totalPlayTimeSec);
    if (deaths != null) writeStored(KEY_DEATHS, deaths);
    if (playtime != null) writeStored(KEY_PLAYTIME, playtime);
  } catch {
    /* optional */
  }
}

/** Pull server records and overwrite local bests (e.g. after admin deletes cloud scores). */
export async function syncLocalRecordsFromServer(): Promise<void> {
  await syncLocalStatsFromLeaderboard();
}

const KEY_ACCOUNT_SYNC_USER_ID = "accountSyncUserId";

function getLastSyncedUserId(): string | null {
  const v = localStorage.getItem(KEY_ACCOUNT_SYNC_USER_ID);
  return v && v.trim() ? v.trim() : null;
}

function setLastSyncedUserId(userId: string): void {
  localStorage.setItem(KEY_ACCOUNT_SYNC_USER_ID, userId);
}

function clearLastSyncedUserId(): void {
  localStorage.removeItem(KEY_ACCOUNT_SYNC_USER_ID);
}

/** Last user id whose cloud payload was applied to this device (localStorage). */
export function getAccountSyncUserId(): string | null {
  return getLastSyncedUserId();
}

/**
 * True when signing in: load only that account's cloud data (never merge guest / other-account local).
 * Merge local+remote only when refreshing the same signed-in user (multi-device sync).
 */
function shouldImportRemoteAccountOnly(userId: string): boolean {
  return getLastSyncedUserId() !== userId;
}

/** Clear stale local account data as soon as the signed-in user changes (before async pull). */
export function prepareLocalAccountForUser(userId: string): void {
  if (getLastSyncedUserId() === userId) return;
  clearLocalAccountData();
}

let pushTimer: ReturnType<typeof setTimeout> | null = null;
let pullInFlight: Promise<AccountDataPayloadV1 | null> | null = null;
let signedIn = false;
let activeUserId: string | null = null;
let syncReady = false;

function cancelPushTimer(): void {
  if (pushTimer) {
    clearTimeout(pushTimer);
    pushTimer = null;
  }
}

/** True after pull finished for the active signed-in user (safe to push / player-stats). */
export function isAccountSyncReady(): boolean {
  return signedIn && syncReady && !!activeUserId && activeUserId === getLastSyncedUserId();
}

export function setAccountSyncSignedIn(on: boolean, userId?: string | null): void {
  if (!on) {
    signedIn = false;
    activeUserId = null;
    syncReady = false;
    clearLastSyncedUserId();
    cancelPushTimer();
    return;
  }
  signedIn = true;
  if (userId && userId !== activeUserId) {
    syncReady = false;
    cancelPushTimer();
  }
  if (userId) activeUserId = userId;
}

export function schedulePushAccountData(force = false): void {
  if (!isAccountSyncReady()) return;
  if (force) {
    cancelPushTimer();
    void pushAccountDataNow();
    return;
  }
  if (pushTimer) return;
  pushTimer = setTimeout(() => {
    pushTimer = null;
    void pushAccountDataNow();
  }, 2500);
}

async function pushAccountDataNow(): Promise<void> {
  if (!isAccountSyncReady()) return;
  try {
    const local = collectLocalAccountPayload();
    const remote = (await fetchAccountData()) ?? emptyAccountPayload();
    const merged = mergeAccountPayloads(local, remote);
    await patchAccountData(merged);
  } catch {
    /* optional cloud */
  }
}

/** Push pending account payload before session cookie changes (login / logout). */
export async function flushAccountDataSync(): Promise<void> {
  cancelPushTimer();
  await pushAccountDataNow();
}

/** True while account data is being pulled from the server. */
export function isAccountPullInFlight(): boolean {
  return pullInFlight != null;
}

/** Pull cloud account data; on account switch imports remote without uploading guest local data. */
export async function pullAccountDataSync(userId: string): Promise<AccountDataPayloadV1 | null> {
  if (!signedIn || !userId) return null;
  if (pullInFlight) return pullInFlight;
  syncReady = false;
  cancelPushTimer();
  activeUserId = userId;

  // Snapshot streaks before prepareLocal may clear guest/local cache on first login.
  const priorUser = getLastSyncedUserId();
  const canCarryStreaks = priorUser === null || priorUser === userId;
  const localStreaksSnap = canCarryStreaks ? getPlayStreaksState() : null;

  prepareLocalAccountForUser(userId);
  pullInFlight = (async () => {
    try {
      let remote = (await fetchAccountData()) ?? emptyAccountPayload();
      remote = await enrichWithCloudLeaderboardBests(remote);

      if (shouldImportRemoteAccountOnly(userId)) {
        clearLocalAccountData();
        const playStreaks = mergePlayStreaks(localStreaksSnap, remote.playStreaks);
        const toApply: AccountDataPayloadV1 = { ...remote, playStreaks };
        applyAccountPayloadToLocal(toApply, { fullReplace: true });
        const saved = await patchAccountData(toApply);
        if (saved?.playStreaks) applyPlayStreaksFromAccount(saved, { replace: true });
        await syncPlayStreaksFromServer({ pushIfLocalHigher: true });
        setLastSyncedUserId(userId);
        syncReady = true;
        return saved ?? toApply;
      }

      const local = collectLocalAccountPayload();
      const merged = mergeAccountPayloads(local, remote);
      // Skip dots on this apply — a concurrent /api/dots mutation must not be overwritten
      // by the pre-PATCH remote snapshot baked into `merged`.
      const dotsGenAtPull = getDotsMutationGen();
      applyAccountPayloadToLocal(merged);
      const saved = await patchAccountData(merged);
      if (saved?.playStreaks) applyPlayStreaksFromAccount(saved, { replace: true });
      // Refresh dots from the saved server payload only if nothing else mutated them
      // while this pull was in flight (LootDrop spend, run-reward, shop, etc.).
      if (saved?.dots && getDotsMutationGen() === dotsGenAtPull) {
        applyDotsFromAccountPayload(saved);
      }
      // Dedicated streaks endpoint — catch up if account blob was stale vs /api/play-streaks writes.
      await syncPlayStreaksFromServer({ pushIfLocalHigher: true });
      setLastSyncedUserId(userId);
      syncReady = true;
      return saved ?? merged;
    } catch {
      syncReady = false;
      return null;
    } finally {
      pullInFlight = null;
    }
  })();
  return pullInFlight;
}

setPlayStreakAccountPushScheduler(() => {
  schedulePushAccountData(true);
});
